import {useEffect} from "react";
import {useFormContext} from "react-hook-form";

import {InputField} from "@/components/form/fields/InputField/InputField.tsx";
import {ServerErrors} from "@/lib/types/common.ts";
import {IntegrationFormData} from "@/lib/types/integrations.ts";
import {SetupInfo} from "@/pages/integrations/edit/subForms/components/SetupInfo.tsx";
import {IntegrationModuleInfo, IntegrationModules} from "@/pages/integrations/integrations.types.ts";

export type FormValuesKey = keyof IntegrationFormData;

interface LogzFormProps {
  serverErrors?: ServerErrors;
}

export const LogzForm = ({serverErrors}: LogzFormProps) => {
  const {setError} = useFormContext();

  useEffect(() => {
    if (serverErrors) {
      Object.keys(serverErrors)
        .filter((k) => k !== "__all__")
        .forEach((key) => setError(`settings.${key}` as FormValuesKey, {message: serverErrors[key][0]}));
    }
  }, [setError, serverErrors]);

  return (
    <>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3">
          <InputField
            name="settings.listener_url"
            label="Listener Domain"
            required={true}
            tooltip="The Listener domain name that logs and metrics are sent to."
            maxLength={100}
          />
        </div>
        <div className="col-6 mb-3">
          <InputField
            name="settings.logs_shipping_token"
            label="Logs Shipping Token"
            required={true}
            maxLength={32}
            autoFocus={true}
          />
        </div>
      </div>
      <div className="row row-cols-2 mb-3">
        <div className="col-6 mb-3">
          <InputField
            name="settings.metrics_shipping_token"
            label="Metrics Shipping Token"
            required={true}
            maxLength={32}
          />
        </div>
      </div>
      <SetupInfo metricsWarning={true}>
        <div className="p-2">
          Uptime.com alerts are sent to Logz.io as Logs and response times are sent to Logz.io as Metrics. In the
          Logz.io app sidebar, go to <strong>Settings &gt; Manage tokens</strong>, then click{" "}
          <strong>Data shipping tokens</strong>. Copy the <strong>Listener URL</strong> value into the corresponding
          field above.
        </div>
        <div className="p-2">
          Select <strong>Logs</strong> at the top, then copy the <strong>Token</strong> value into the &quot;Logs
          Shipping Token&quot; field. Then, select <strong>Metrics</strong> at the top, and copy the{" "}
          <strong>Token</strong> value into the &quot;Metrics Shipping Token&quot; field.
        </div>
        <div className="p-2">
          Once sent successfully, metrics will appear in Logz.io named &quot;<strong>uptime_response_time</strong>&quot;
          with appropriate labels identifying the check and locations.
        </div>
        <div className="p-2">
          <strong>NOTE:</strong> If you do not have a Metrics Token in Logz.io, refer to the{" "}
          <a target="_blank" href="https://app.logz.io/#/dashboard/settings/manage-accounts">
            Manage Accounts
          </a>{" "}
          page to create a metrics account or contact Logz.io support for assistance.
        </div>
        <div className="p-2">
          <a target="_blank" href="https://support.uptime.com/hc/en-us/articles/9256071555228-Configuring-Logz-io">
            View {IntegrationModuleInfo[IntegrationModules.LOGZ].label} Support Article &rarr;
          </a>
        </div>
      </SetupInfo>
    </>
  );
};
